// popup.service.ts
import { Injectable } from '@angular/core';
import Swal from 'sweetalert2';
import { environment } from "../../environments/environment";
import { Router } from "@angular/router";
import { BehaviorSubject } from "rxjs";

export interface NotificationItem {
  id: number;
  message: string;
  title: string;
  type: 'success' | 'error' | 'warning' | 'info';
  action?: { url: string; text: string };
}

@Injectable({
  providedIn: 'root'
})
export class PopupService {

  // Keep these for backwards compatibility
  public notificationVisible = false;
  public notificationType: 'success' | 'error' | 'warning' | 'info' = 'success';
  public notificationMessage = '';
  public notificationTitle = '';
  public notificationAction?: { text: string; url: string };

  private currentId = 0;
  private notifications: NotificationItem[] = [];
  notifications$ = new BehaviorSubject<NotificationItem[]>([]);

  // Track recently dispatched notifications to prevent accidental duplicates
  private recentNotifications: {
    message: string;
    type: string;
    timestamp: number;
  }[] = [];

  // Throttle period for preventing duplicate notifications (ms)
  private throttlePeriod = 300; // 300ms

  constructor(
    private routerService: Router,
  ) {}

  success(title, body, button) {
    Swal.fire({
      title: title,
      html: '<div class="text-sm text-gray-700">' + body + '</div>',
      // icon: 'success',
      confirmButtonText: button,
      animation: false,
    });
  }

  /** Reusable yes/no confirm dialog. Resolves true when the user confirms. */
  async confirm(
    title: string,
    body: string,
    confirmText = 'Confirm',
    cancelText = 'Cancel',
  ): Promise<boolean> {
    const result = await Swal.fire({
      title,
      html: '<div class="text-sm text-gray-700">' + body + '</div>',
      showCancelButton: true,
      confirmButtonText: confirmText,
      cancelButtonText: cancelText,
      animation: false,
    });
    return result.isConfirmed;
  }

  info(title, body) {
    Swal.fire({
      html: `
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 1.5rem;">
          <h1 class="text-xl font-semibold leading-6 text-secondary-900">${title}</h1>

          <!-- Close -->
          <button type="button" id="swal-close-button" class="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none">
              <span class="sr-only">Close</span>
              <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
        </div>

        <!-- Body -->
        <div class="text-sm text-start text-secondary font-light max-h-[70vh] overflow-y-auto scrollbar-thin scrollbar-track-transparent scrollbar-thumb-gray-200 hover:scrollbar-thumb-transparent">
          ${body}
        </div>
    `,
      didOpen: () => {
        const closeButton = document.getElementById('swal-close-button');
        closeButton?.addEventListener('click', () => Swal.close());
      },
      animation: false,
      showConfirmButton: false,
      customClass: {
        popup: 'swal-default-popup',
        htmlContainer: 'swal-default-container'
      }
    });
  }

  // New method for wide modals (specifically for insights grid)
  infoWide(title, body) {
    Swal.fire({
      html: `
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 1.5rem;">
          <h1 class="text-xl font-semibold leading-6 text-secondary-900">${title}</h1>

          <!-- Close -->
          <button type="button" id="swal-close-button" class="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none">
              <span class="sr-only">Close</span>
              <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
        </div>

        <!-- Body -->
        <div class="text-sm text-start text-secondary font-light h-[70vh] max-h-[70vh] overflow-y-auto scrollbar-thin scrollbar-track-transparent scrollbar-thumb-gray-200 hover:scrollbar-thumb-transparent">
          ${body}
        </div>
    `,
      didOpen: () => {
        const closeButton = document.getElementById('swal-close-button');
        closeButton?.addEventListener('click', () => Swal.close());
      },
      animation: false,
      showConfirmButton: false,
      width: '90%', // Use 90% of viewport width
      customClass: {
        popup: 'swal-wide-popup',
        htmlContainer: 'swal-wide-container'
      }
    });
  }

  career(title, body, button) {
    Swal.fire({
      title: title,
      html: '<div class="text-sm text-secondary">' + body + '</div>',
      // icon: 'info',
      confirmButtonText: button,
      animation: false,
    });
  }

  error(title, body, button) {
    Swal.fire({
      title: title,
      html: '<div class="text-xs text-secondary">' + body + '</div>',
      // icon: 'error',
      confirmButtonText: button,
      animation: false,
    });
  }

  report_error(title, body, button) {
    Swal.fire({
      title: title,
      html: '<div class="text-xs text-secondary">' + body + '</div>',
      icon: 'error',
      showConfirmButton: true,
      confirmButtonText: 'Back to Dashboard'
    }).then((result) => {
      if (result.isConfirmed) {
        // Redirect to a page
        this.routerService.navigate(['/dashboard']);
      }
    });
  }

  subscription_expired(title, body, button) {
    Swal.fire({
      title: title,
      html: '<div class="text-xs text-gray-700">' + body + '</div>',
      icon: 'error',
      showConfirmButton: true,
      allowOutsideClick: false,
      allowEscapeKey: false,
      confirmButtonText: 'Update Subscription'
    }).then((result) => {
      if (result.isConfirmed) {
        // Redirect to a page
        window.location.href = environment.billing_url;
      }
    });
  }

  modal(title, body, button) {
    Swal.fire({
      title: title,
      html: '<div class="text-xs text-gray-700">' + body + '</div>',
      confirmButtonText: button
    });
  }

  notify(
    message: any,
    title = "",
    type: 'success' | 'error' | 'warning' | 'info' = "success",
    duration = environment.notification_duration,
    action = {url: "", text: ""}
  ) {
    // Update legacy properties
    this.notificationMessage = message;
    this.notificationTitle = title;
    this.notificationType = type;
    this.notificationAction = action;
    this.notificationVisible = true;

    // Check for accidental rapid duplicates (e.g., double-clicks)
    const now = Date.now();
    const recentDuplicate = this.recentNotifications.find(n =>
      n.message === message &&
      n.type === type &&
      (now - n.timestamp) < this.throttlePeriod
    );

    if (recentDuplicate) {
      // Skip this notification as it's likely an accidental duplicate
      console.debug('Skipping duplicate notification:', message);
      return;
    }

    // Add to recent notifications for throttling
    this.recentNotifications.push({
      message,
      type,
      timestamp: now
    });

    // Clean up old entries from recentNotifications
    this.recentNotifications = this.recentNotifications.filter(
      n => (now - n.timestamp) < this.throttlePeriod * 2
    );

    const notification: NotificationItem = {
      id: this.currentId++,
      message,
      title,
      type,
      action: action && action.url ? action : undefined
    };

    this.notifications.push(notification);
    this.notifications$.next([...this.notifications]);

    setTimeout(() => {
      this.removeNotification(notification.id);
    }, duration);
  }

  removeNotification(id: number) {
    const idx = this.notifications.findIndex(n => n.id === id);
    if (idx !== -1) {
      this.notifications.splice(idx, 1);
      this.notifications$.next([...this.notifications]);

      // Update legacy properties if this was the current notification
      if (this.notifications.length === 0) {
        this.notificationVisible = false;
      }
    }
  }

  resetNotification() {
    this.notificationVisible = false;
    this.notificationMessage = '';
    this.notificationTitle = '';
    this.notificationAction = undefined;
    this.notifications = [];
    this.notifications$.next([]);
  }
}
